'use client';

import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { Contact, GeneralOpeningHour, SpecialOpeningHour } from "@/app/lib/generated/graphql-types";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import { toggleDraftMode } from "@/app/lib/actions/editing";
import FloatingEditorMenu from "@/app/features/shared/__to_be_integrated/FloatingEditorMenu";
import { getContactCached } from "@/app/lib/contact/queries";
import {
    getGeneralOpeningHoursCached,
    getSpecialOpeningHoursCached
} from "@/app/features/sites/contact/data-access/queries";

type ContactEditorContextType = {
    contact: Contact;
    generalOpeningHours: GeneralOpeningHour[];
    specialOpeningHours: SpecialOpeningHour[];
}

const SiteContactEditorContext = createContext<ContactEditorContextType | undefined>(undefined);

export function useSiteContactEditor() {
    const context = useContext(SiteContactEditorContext);
    if (!context) {
        throw new Error("useContactEditorContext must be used within a SiteContactEditorProvider");
    }
    return context;
}

interface SiteContactEditorProviderProps {
    children: ReactNode;
    isDraftModeEnabled: boolean;
}

export default function SiteContactEditorProvider({ children, isDraftModeEnabled }: SiteContactEditorProviderProps) {
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<{ contact: Contact, generalOpeningHours: GeneralOpeningHour[]; specialOpeningHours: SpecialOpeningHour[]; } | undefined>(undefined);


    useEffect(() => {
        let isMounted = true;

        (async () => {
            const [
                { data: contactData, error: contactError },
                { data: openingHours, error: openingHoursError },
                { data: specialOpeningHours, error: specialOpeningHoursError }
            ] = await Promise.all([
                getContactCached(),
                getGeneralOpeningHoursCached(),
                getSpecialOpeningHoursCached(),
            ])

            if (contactError || !contactData) throw contactError?.message || new Error("Failed to load contact data");
            if (openingHoursError || !openingHours) throw openingHoursError?.message || new Error("Failed to load opening hours data");
            if (specialOpeningHoursError || !specialOpeningHours) throw specialOpeningHoursError?.message || new Error("Failed to load special opening hours data");

            if (!isMounted) return;

            setData({
                contact: contactData,
                generalOpeningHours: openingHours,
                specialOpeningHours: specialOpeningHours
            })
            setIsLoading(false);
        })()

        return () => {
            isMounted = false;
        };
    }, [])

    if (isLoading) return <LoadingSpinner />

    if (!data) {
        throw new Error("Contact data is undefined in SiteContactEditorProvider");
    }

    const { contact, generalOpeningHours, specialOpeningHours } = data;

    return (
        <SiteContactEditorContext.Provider value={{ contact, generalOpeningHours, specialOpeningHours }}>
            {children}
            <FloatingEditorMenu
                isDraftModeEnabled={isDraftModeEnabled}
                toggleDraftMode={toggleDraftMode}
            />
        </SiteContactEditorContext.Provider>
    )
}
